import { ChangeEvent, FC, MouseEvent, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { Buttons, Form, FormElements, H1 } from '../../../common/AddEditFormLayout/AddEditFormLayout.styles';
import Header from '../../../common/Header/Header';
import { H2 } from '../../../common/Headings/Headings.styles';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import Button from '../../../components/Button/Button';
import Dropdown from '../../../components/Dropdown/Dropdown';
import { Options } from '../../../components/Dropdown/Dropdown.types';
import Input from '../../../components/Input/Input';
import { OPTIONS_PLACEHOLDER } from '../../../constants/Dropdown';
import { useManInvGroups } from '../../../services/manInv/manInv.service';
import { ManInvGroup } from '../../../services/manInv/manInv.types';
import { useStoreMarketsRecord } from '../../../services/store/store.service';
import { useEditStoreControlRecord, useStoreControlRecord } from '../../../services/storeControl/storeControl.service';
import { StoreInfoFormValues } from '../../../services/storeControl/storeControl.types';
import { useGenericErrorMessage } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import useNavigateWithToast from '../../../utils/useNavigateWithToast/useNavigateWithToast';
import {
  DropdownWrapper,
  HorizontalLine,
  InputWrapper,
  InputWrapperOne,
  InputWrapperThree,
  InputWrapperTwo,
} from '../StoreRecord.styles';
import { WEB_MENU, WebMenuKeys } from '../../../constants/ApiEnums';

const EditStoreRecord: FC = () => {
  const { storeNo } = useParams();
  const _storeNo = storeNo as string;
  const { data: manInvGroupsData } = useManInvGroups(true);
  const { data: storeMarketsRecordData } = useStoreMarketsRecord(_storeNo, true);
  const { data: storeControlRecordData } = useStoreControlRecord(_storeNo, true);
  const { mutate, isError, isLoading, error } = useEditStoreControlRecord(_storeNo);
  const navigateWithToast = useNavigateWithToast();
  const queryClient = useQueryClient();

  useGenericErrorMessage([{ isError, error }]);

  const [manInvGroupOptions, setManInvGroupOptions] = useState<Options[]>([]);
  const [formValues, setFormValues] = useState<StoreInfoFormValues>({
    manInvGroup: OPTIONS_PLACEHOLDER,
    webMenu: OPTIONS_PLACEHOLDER,
    couponGroupId: 'NOCOUP',
  });


  const [initialFormValues, setInitialFormValues] = useState<StoreInfoFormValues>({
    manInvGroup: OPTIONS_PLACEHOLDER,
    webMenu: OPTIONS_PLACEHOLDER,
    couponGroupId: 'NOCOUP',
  });

  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const allManInvGroups = manInvGroupsData?.data || [];
  const storeMarketInfo = storeMarketsRecordData?.data || undefined;

  const checkForUnsavedChanges = () => {
    if (formValues.manInvGroup !== initialFormValues.manInvGroup || formValues.webMenu !== initialFormValues.webMenu) {
      setUnsavedChanges(true);
      return;
    }
    setUnsavedChanges(false);
  };

  useEffect(() => {
    const storeRecordInfo = storeControlRecordData?.data || undefined;
    if (storeRecordInfo) {
      setFormValues({
        couponGroupId: storeRecordInfo?.couponGroupId,
        manInvGroup: storeRecordInfo?.maninvGroupId?.toString() || OPTIONS_PLACEHOLDER,
        webMenu: storeRecordInfo?.webMenu?.toString() || OPTIONS_PLACEHOLDER
      });
      setInitialFormValues({
        couponGroupId: storeRecordInfo?.couponGroupId,
        manInvGroup: storeRecordInfo?.maninvGroupId?.toString() || OPTIONS_PLACEHOLDER,
        webMenu: storeRecordInfo?.webMenu?.toString() || OPTIONS_PLACEHOLDER,
      });
    }
  }, [storeControlRecordData]);

  useEffect(() => {
    if (allManInvGroups) {
      setManInvGroupOptions(
        allManInvGroups
          .sort((manInvOne, manInvTwo) => {
            return manInvOne?.id - manInvTwo?.id;
          })
          .map((manInvGroup: ManInvGroup) => {
            return { value: manInvGroup?.id?.toString(), label: manInvGroup?.description };
          })
      );
    }
  }, [manInvGroupsData]);

  useEffect(() => {
    checkForUnsavedChanges();
  }, [formValues]);

  const handleSelectManInv = (event: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };
  const handleSelectWebMenu = (event: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  }

  const handleSave = (event: MouseEvent) => {
    event.preventDefault();
    setUnsavedChanges(false);
    mutate(
      {
        storeNo: _storeNo,
        mxref: _storeNo,
        couponGroupId: formValues?.couponGroupId,
        maninvGroupId: Number(formValues?.manInvGroup),
        webMenu: formValues?.webMenu
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('store-controls');
          navigateWithToast('/storecontrol', {
            label: 'Store record has been updated.',
            id: 'storeRecordUpdateSuccess',
          });
        },
      }
    );
  };


  return (
    <>
      <Header breadcrumb={{ label: 'Back to Table', to: '/storecontrol' }} title='Store Control' />
      <Form>
        <FormElements>
          <H1>Edit Store Record</H1>
          <H2>Market Info</H2>
          <InputWrapper>
            <InputWrapperOne>
              <Input label='Store' disabled id='disabledStoreInput' value={storeMarketInfo?.storeNo?.toString()} />
            </InputWrapperOne>
            <InputWrapperTwo>
              <Input label='Coupon Group' disabled id='disabledMenuIdInput' value={formValues?.couponGroupId} />
              <Input label='DMA' disabled id='disabledDmaInput' value={storeMarketInfo?.dma} />
              <Input label='Division' disabled id='disabledDivisionInput' value={storeMarketInfo?.division} />
            </InputWrapperTwo>
            <InputWrapperThree>
              <Input label='Concept' disabled id='disabledConceptInput' value={storeMarketInfo?.conceptType} />
              <Input label='Market' disabled id='disabledMarketInput' value={storeMarketInfo?.marketNo} />
            </InputWrapperThree>
          </InputWrapper>
          <HorizontalLine />
          <H2>Store Info</H2>
          <DropdownWrapper>
            <Dropdown
              label='Mandatory Inventory Group'
              id='manInvGroup'
              value={formValues?.manInvGroup}
              options={manInvGroupOptions}
              onChange={handleSelectManInv}
            />
            <Dropdown
              label='WebMenu'
              id='webMenu'
              value={formValues?.webMenu}
              options={Object.keys(WEB_MENU).map((key: any) => {
                return { value: key, label: `${WEB_MENU[key as WebMenuKeys]}` };
              })}
              onChange={handleSelectWebMenu}
            />
          </DropdownWrapper>
        </FormElements>
        <Buttons>
          {formValues.manInvGroup === OPTIONS_PLACEHOLDER || formValues.webMenu === OPTIONS_PLACEHOLDER ? (
            <Button label='Save' onClick={handleSave} isLoading={isLoading} disabled />
          ): (
            <Button label='Save' onClick={handleSave} isLoading={isLoading} disabled={!unsavedChanges} />
          )}
        </Buttons>
      </Form>
      <UnsavedChangesModal unsavedChangesExist={unsavedChanges} />
    </>
  );
};

export default EditStoreRecord;
